import React from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ConfirmationModal from '@/common/components/modals/general/ConfirmationModal';
import { DeleteIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import { ActivityData, CourseData, SubunitData, SubunitId, UnitId } from 'course-types';

function DeleteSubunitModal() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const activeSubunitId: SubunitId | undefined = useAppSelector(() => store.getState().subunit.id);
  const activeUnitId: UnitId | undefined = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  // Helper functions for data access
  const getActiveUnit = () => activeCourseData.units[activeUnitId as UnitId];
  const getActiveSubunit = () => getActiveUnit()?.subunits[activeSubunitId as SubunitId];

  const subunitName = getActiveSubunit()?.name ?? 'Undefined Subunit';

  const handleDeleteSubunit = async () => {
    try {
      const response = await (
        await Request.delete('course/removeSubunit', { subunitId: activeSubunitId })
      )?.json();

      if (!response || response.error || !response.status) {
        throw new Error('Failed to delete subunit');
      }

      // Helper function to remove subunit from course data
      const removeSubunitFromCourseData = (): CourseData => {
        const activeUnit = getActiveUnit();
        const updatedSubunits = { ...activeUnit.subunits };
        delete updatedSubunits[activeSubunitId as SubunitId];

        return {
          ...activeCourseData,
          units: {
            ...activeCourseData.units,
            [activeUnitId as UnitId]: {
              ...activeUnit,
              subunits: updatedSubunits,
            },
          },
        };
      };

      const newCourseData = removeSubunitFromCourseData();

      // Helper function to reindex unit content after subunit deletion
      const reindexUnitContent = (courseData: CourseData): void => {
        const activeUnit = courseData.units[activeUnitId as UnitId];

        // Create typed content items for sorting
        type SubunitItem = { itemType: 'subunit'; subunitId: string; data: SubunitData };
        type ActivityItem = { itemType: 'activity'; data: ActivityData };

        const subunitItems: SubunitItem[] = Object.entries(activeUnit.subunits).map(
          ([subunitId, subunitData]) => ({
            itemType: 'subunit' as const,
            subunitId,
            data: subunitData,
          }),
        );

        const activityItems: ActivityItem[] = activeUnit.activities.map((activity) => ({
          itemType: 'activity' as const,
          data: activity,
        }));

        // Combine and sort all content by index
        const allContent = [...subunitItems, ...activityItems].sort(
          (a, b) => (a.data.index ?? -1) - (b.data.index ?? -1),
        );

        // Reindex and separate activities from subunits
        const reindexedActivities: ActivityData[] = [];
        allContent.forEach((item, newIndex) => {
          if (item.itemType === 'activity') {
            reindexedActivities.push({ ...item.data, index: newIndex });
          } else if (item.data.index !== newIndex) {
            // Update subunit index if changed
            activeUnit.subunits[item.subunitId as SubunitId] = {
              ...item.data,
              index: newIndex,
            };
          }
        });

        activeUnit.activities = reindexedActivities;
      };

      reindexUnitContent(newCourseData);

      dispatch(setActiveCourseData(newCourseData));
      toast.success('Subunit deleted successfully');
    } catch (error) {
      console.error('Error deleting subunit:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete subunit');
      throw error;
    }
  };

  return (
    <ConfirmationModal
      modalName='DeleteSubunit'
      title={`Delete "${subunitName}"`}
      subtitle='This action cannot be undone'
      message='Are you sure you want to delete this subunit?'
      description='All content within this subunit will be permanently deleted. This includes all activities and associated resources.'
      confirmText='Delete Subunit'
      confirmIcon={<DeleteIcon className='w-5 h-5' />}
      isDestructive={true}
      onConfirm={handleDeleteSubunit}
    />
  );
}

export default DeleteSubunitModal;
